import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';

import { UseOutletContextAdmin } from './Admin';
import { StateLanguage } from '../../../languages/config/StateLanguage';
import { useSubscription } from '../../../contexts/SubscriptionContext';
import { BillingDA } from '../../../services/admin/billing.service';
import { dateFormat } from '../../../scripts/date.time.format.script';
import { payInvoice } from '../../../scripts/billing.payment';
import { GlobalSwal } from '../../../styles/swal.style';
import { BillingInvoice, BillingPayment } from '../../../models/billing.model';
import AppPagination from '../../../components/element/Pagination';
import AppPreloader from '../../../components/Preloader';

const INVOICE_ARN = 'digital:auth:entity:subscription:*';

const SubscriptionInvoicesPage: React.FunctionComponent = () => {
  const {setRoute, companyForUser} = UseOutletContextAdmin();
  const {lang} = StateLanguage();
  const navigate = useNavigate();
  const { can: canSub } = useSubscription();
  const [mounted, setMounted] = useState(false);

  // Invoices state
  const [invoices, setInvoices] = useState<BillingInvoice[] | null>(null);
  const [invoicesError, setInvoicesError] = useState(false);
  const [invoicePagination, setInvoicePagination] = useState({current: 1, limit: 20, total: 0, pages: [1] as number[]});

  // Payments state
  const [payments, setPayments] = useState<BillingPayment[] | null>(null);
  const [paymentsError, setPaymentsError] = useState(false);
  const [paymentPagination, setPaymentPagination] = useState({current: 1, limit: 20, total: 0, pages: [1] as number[]});

  // Invoice detail
  const [selectedInvoice, setSelectedInvoice] = useState<BillingInvoice | null>(null);

  // Pay button loading per invoice
  const [payingInvoiceId, setPayingInvoiceId] = useState<string | null>(null);

  const canManage = canSub('manage', INVOICE_ARN).allowed;
  const canRead = canSub('read', INVOICE_ARN).allowed;

  useEffect(() => {
    setMounted(true);
    setRoute({path: {root: lang.labels.administration, branch: lang.labels.billingInvoices}, company: true});
    return () => setMounted(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fmtDate = useCallback((iso?: string): string => {
    if (!iso) return '';
    return dateFormat(lang.code, iso);
  }, [lang.code]);

  const interpolate = (template: string, vars: Record<string, string | number>): string => {
    let result = template;
    for (const [key, value] of Object.entries(vars)) {
      result = result.replace(`{${key}}`, String(value));
    }
    return result;
  };

  const formatAmount = (amount?: number, currency?: string): string => {
    if (amount === undefined || amount === null) return '–';
    return `${currency ?? 'CHF'} ${amount.toFixed(2)}`;
  };

  // --- Load invoices ---
  const loadInvoices = useCallback(async (page: number = 1) => {
    if (!companyForUser?.id) return;

    setInvoicesError(false);
    try {
      const response = await BillingDA.getInvoices(companyForUser.id, page, invoicePagination.limit);
      if (response.status === 200 && response.data) {
        const data = response.data;
        const items: BillingInvoice[] = data.items ?? data.invoices ?? (Array.isArray(data) ? data : []);
        setInvoices(items);

        if (data.pagination) {
          const totalPages = data.pagination.pages || 1;
          const pagesArr: number[] = [];
          for (let i = 1; i <= totalPages; i++) pagesArr.push(i);
          setInvoicePagination(prev => ({
            ...prev,
            current: data.pagination.page || page,
            total: data.pagination.total || 0,
            pages: pagesArr,
          }));
        }
      } else {
        setInvoices([]);
      }
    } catch {
      setInvoices(null);
      setInvoicesError(true);
    }
  }, [companyForUser, invoicePagination.limit]);

  // --- Load payments ---
  const loadPayments = useCallback(async (page: number = 1) => {
    if (!companyForUser?.id) return;

    setPaymentsError(false);
    try {
      const response = await BillingDA.getPayments(companyForUser.id, page, paymentPagination.limit);
      if (response.status === 200 && response.data) {
        const data = response.data;
        const items: BillingPayment[] = data.items ?? data.payments ?? (Array.isArray(data) ? data : []);
        setPayments(items);

        if (data.pagination) {
          const totalPages = data.pagination.pages || 1;
          const pagesArr: number[] = [];
          for (let i = 1; i <= totalPages; i++) pagesArr.push(i);
          setPaymentPagination(prev => ({
            ...prev,
            current: data.pagination.page || page,
            total: data.pagination.total || 0,
            pages: pagesArr,
          }));
        }
      } else {
        setPayments([]);
      }
    } catch {
      setPayments(null);
      setPaymentsError(true);
    }
  }, [companyForUser, paymentPagination.limit]);

  // --- Load invoice detail ---
  const loadInvoiceDetail = useCallback(async (invoiceId: string) => {
    if (!companyForUser?.id) return;

    try {
      const response = await BillingDA.getInvoice(companyForUser.id, invoiceId);
      if (response.status === 200 && response.data) {
        setSelectedInvoice(response.data);
      }
    } catch {
      // detail load failed — keep list visible
    }
  }, [companyForUser]);

  useEffect(() => {
    if (canRead) {
      loadInvoices(1);
      loadPayments(1);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canRead]);

  // --- Pay: request token only on click, navigate to /pay/:token ---
  const handlePay = async (invoice: BillingInvoice) => {
    if (!invoice.payable || !invoice.invoice_id || !companyForUser?.id) return;

    setPayingInvoiceId(invoice.invoice_id);
    try {
      await payInvoice(invoice, companyForUser.id, navigate, {
        error: lang.labels.sorryLooksLikeThereAreSomeErrorstryAgain,
        ok: lang.labels.okGotIt,
      });
    } finally {
      setPayingInvoiceId(null);
    }
  };

  // --- Invoice status helpers ---
  const invoiceStatusLabel = (status?: string): string => {
    switch (status) {
      case 'open': return lang.labels.billingInvoiceStatusOpen;
      case 'paid': return lang.labels.billingInvoiceStatusPaid;
      case 'void': return lang.labels.billingInvoiceStatusVoid;
      case 'draft': return lang.labels.billingInvoiceStatusDraft;
      case 'uncollectible': return lang.labels.billingInvoiceStatusUncollectible;
      default: return status ?? '';
    }
  };

  const invoiceStatusBadge = (invoice: BillingInvoice): string => {
    const isOverdue = invoice.payment_status === 'open' && invoice.valid_until && new Date(invoice.valid_until) < new Date();
    if (isOverdue) return 'badge-light-danger';
    switch (invoice.payment_status) {
      case 'open': return 'badge-light-warning';
      case 'paid': return 'badge-light-success';
      case 'void': return 'badge-light-dark';
      case 'draft': return 'badge-light-secondary';
      case 'uncollectible': return 'badge-light-danger';
      default: return 'badge-light-secondary';
    }
  };

  const isOverdue = (invoice: BillingInvoice): boolean => {
    return invoice.payment_status === 'open' && !!invoice.valid_until && new Date(invoice.valid_until) < new Date();
  };

  // --- Payment status helpers ---
  const paymentStatusLabel = (status?: string): string => {
    switch (status) {
      case 'succeeded': return lang.labels.billingPaymentSucceeded;
      case 'failed': return lang.labels.billingPaymentFailed;
      case 'pending': return lang.labels.billingPaymentPending;
      case 'refunded': return lang.labels.billingPaymentRefunded;
      default: return status ?? '';
    }
  };

  const paymentStatusBadge = (status?: string): string => {
    switch (status) {
      case 'succeeded': return 'badge-light-success';
      case 'failed': return 'badge-light-danger';
      case 'pending': return 'badge-light-warning';
      case 'refunded': return 'badge-light-info';
      default: return 'badge-light-secondary';
    }
  };

  if (!mounted) return null;

  return (
    <div className="d-flex flex-column flex-column-fluid">
      {/* Invoice detail overlay */}
      {selectedInvoice && (
        <div className="card card-flush mb-5">
          <div className="card-header">
            <div className="card-title">
              <h3 className="fw-bolder">{lang.labels.billingInvoiceDetail}</h3>
            </div>
            <div className="card-toolbar">
              <button className="btn btn-sm btn-light" type="button" onClick={() => setSelectedInvoice(null)}>
                {lang.labels.cancel}
              </button>
            </div>
          </div>
          <div className="card-body">
            <table className="table gs-0 gy-2 m-0">
              <tbody>
                <tr>
                  <td className="w-200px fw-bold text-muted">{lang.labels.billingInvoiceNumber}:</td>
                  <td className="min-w-200px fw-bold fs-6">{selectedInvoice.number ?? selectedInvoice.invoice_id}</td>
                </tr>
                <tr>
                  <td className="w-200px fw-bold text-muted">{lang.labels.billingInvoiceDate}:</td>
                  <td className="min-w-200px fw-bold fs-6">{fmtDate(selectedInvoice.date)}</td>
                </tr>
                <tr>
                  <td className="w-200px fw-bold text-muted">{lang.labels.billingInvoiceDueDate}:</td>
                  <td className="min-w-200px fw-bold fs-6">{fmtDate(selectedInvoice.valid_until)}</td>
                </tr>
                <tr>
                  <td className="w-200px fw-bold text-muted">{lang.labels.billingInvoiceAmount}:</td>
                  <td className="min-w-200px fw-bold fs-6">{formatAmount(selectedInvoice.amount, selectedInvoice.currency)}</td>
                </tr>
                <tr>
                  <td className="w-200px fw-bold text-muted">{lang.labels.billingInvoiceStatus}:</td>
                  <td className="min-w-200px">
                    <span className={`badge ${invoiceStatusBadge(selectedInvoice)} fs-7`}>
                      {isOverdue(selectedInvoice) ? lang.labels.billingInvoiceOverdue : invoiceStatusLabel(selectedInvoice.payment_status)}
                    </span>
                  </td>
                </tr>
              </tbody>
            </table>
            <div className="d-flex gap-3 mt-5">
              {canManage && selectedInvoice.payable && (
                <button
                  className="btn btn-sm btn-primary"
                  type="button"
                  disabled={payingInvoiceId === selectedInvoice.invoice_id}
                  onClick={() => handlePay(selectedInvoice)}
                >
                  {payingInvoiceId === selectedInvoice.invoice_id ? lang.labels.billingPaymentPending : lang.labels.billingPay}
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Invoices list */}
      <div className="card card-flush mb-5">
        <div className="card-header">
          <div className="card-title">
            <h3 className="fw-bolder">{lang.labels.billingInvoices}</h3>
          </div>
        </div>
        <div className="card-body pt-0">
          {invoicesError ? (
            <div className="text-center py-10">
              <p className="text-gray-600 fs-6 mb-4">{lang.labels.billingUnavailable}</p>
              <button className="btn btn-sm btn-light-primary" type="button" onClick={() => loadInvoices(invoicePagination.current)}>
                {lang.labels.retry}
              </button>
            </div>
          ) : invoices === null ? (
            <AppPreloader />
          ) : invoices.length === 0 ? (
            <div className="text-center py-10 text-gray-500 fs-6">{lang.labels.billingNoInvoices}</div>
          ) : (
            <div className="dataTables_wrapper">
              <div className="table-responsive">
                <table className="table align-middle table-row-dashed mb-2">
                  <thead>
                    <tr className="text-gray-500 fw-bolder fs-7 text-uppercase">
                      <th>{lang.labels.billingInvoiceNumber}</th>
                      <th>{lang.labels.billingInvoiceDate}</th>
                      <th>{lang.labels.billingInvoiceDueDate}</th>
                      <th className="text-end">{lang.labels.billingInvoiceAmount}</th>
                      <th>{lang.labels.billingInvoiceStatus}</th>
                      {canManage && <th className="text-end"></th>}
                    </tr>
                  </thead>
                  <tbody>
                    {invoices.map(inv => {
                      const overdue = isOverdue(inv);
                      return (
                        <tr key={inv.invoice_id} className={overdue ? 'bg-light-danger' : ''}>
                          <td>
                            <button
                              className="btn btn-link p-0 fw-bold text-primary"
                              type="button"
                              onClick={() => loadInvoiceDetail(inv.invoice_id)}
                            >
                              {inv.number ?? inv.invoice_id}
                            </button>
                          </td>
                          <td>{fmtDate(inv.date)}</td>
                          <td>{fmtDate(inv.valid_until)}</td>
                          <td className="text-end fw-bold">{formatAmount(inv.amount, inv.currency)}</td>
                          <td>
                            <span className={`badge ${invoiceStatusBadge(inv)} fs-7`}>
                              {overdue ? lang.labels.billingInvoiceOverdue : invoiceStatusLabel(inv.payment_status)}
                            </span>
                          </td>
                          {canManage && (
                            <td className="text-end">
                              {inv.payable && (
                                <button
                                  className="btn btn-sm btn-primary"
                                  type="button"
                                  disabled={payingInvoiceId === inv.invoice_id}
                                  onClick={() => handlePay(inv)}
                                >
                                  {payingInvoiceId === inv.invoice_id ? lang.labels.billingPaymentPending : lang.labels.billingPay}
                                </button>
                              )}
                            </td>
                          )}
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              <AppPagination
                showOption={false}
                pagination={invoicePagination}
                loadPagination={(page: number) => loadInvoices(page)}
              />
            </div>
          )}
        </div>
      </div>

      {/* Payments history */}
      <div className="card card-flush mb-5">
        <div className="card-header">
          <div className="card-title">
            <h3 className="fw-bolder">{lang.labels.billingPayments}</h3>
          </div>
        </div>
        <div className="card-body pt-0">
          {paymentsError ? (
            <div className="text-center py-10">
              <p className="text-gray-600 fs-6 mb-4">{lang.labels.billingUnavailable}</p>
              <button className="btn btn-sm btn-light-primary" type="button" onClick={() => loadPayments(paymentPagination.current)}>
                {lang.labels.retry}
              </button>
            </div>
          ) : payments === null ? (
            <AppPreloader />
          ) : payments.length === 0 ? (
            <div className="text-center py-10 text-gray-500 fs-6">{lang.labels.billingNoPayments}</div>
          ) : (
            <div className="dataTables_wrapper">
              <div className="table-responsive">
                <table className="table align-middle table-row-dashed mb-2">
                  <thead>
                    <tr className="text-gray-500 fw-bolder fs-7 text-uppercase">
                      <th>{lang.labels.billingPaymentDate}</th>
                      <th className="text-end">{lang.labels.billingPaymentAmount}</th>
                      <th>{lang.labels.billingPaymentMethod}</th>
                      <th>{lang.labels.billingPaymentResult}</th>
                      <th>{lang.labels.billingRelatedInvoice}</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {payments.map((pmt, idx) => (
                      <tr key={pmt.commerce_invoice_number ?? idx}>
                        <td>{fmtDate(pmt.settled_at)}</td>
                        <td className="text-end fw-bold">{formatAmount(pmt.amount, pmt.currency)}</td>
                        <td>{pmt.method ?? '–'}</td>
                        <td>
                          <span className={`badge ${paymentStatusBadge(pmt.status)} fs-7`}>
                            {paymentStatusLabel(pmt.status)}
                          </span>
                        </td>
                        <td>
                          {pmt.commerce_invoice_number ? (
                            <span className="text-gray-700">{pmt.commerce_invoice_number}</span>
                          ) : '–'}
                        </td>
                        <td className="text-end">
                          {pmt.status === 'failed' && pmt.failure_reason && (
                            <span className="text-danger fs-7">{pmt.failure_reason}</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <AppPagination
                showOption={false}
                pagination={paymentPagination}
                loadPagination={(page: number) => loadPayments(page)}
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default SubscriptionInvoicesPage;
